import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import Popover from "@mui/material/Popover";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import QRCode from "react-qr-code";
import * as global from "../data/global";
import { MuiMaterials } from "../@index.js";
import { createApiEndpoint } from "../index.js";
import {
    WindowChrome,
    WindowChromeStatusBanner,
    ModalBody,
    ModalContentTile,
    CenteredLoadingOverlay,
    outlinedActionButtonSx,
    comparisonFieldSx,
    ScrollHintBody,
} from "./windowChrome/index.js";

function formatDateDisplay(value) {
    if (!value) return "—";
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const MANUFACTURING_LABEL_BASE_URL = "https://grainwave.org";

function deviceLabelUrl(guid) {
    if (!guid) return "";
    return `${MANUFACTURING_LABEL_BASE_URL}/device/${guid}`;
}

/** Named part lines A–Z; blank name rows stay at the bottom for new entries. */
function sortPartLines(lines) {
    const named = lines.filter((line) => (line.name || "").trim());
    const blank = lines.filter((line) => !(line.name || "").trim());
    named.sort((a, b) =>
        (a.name || "").trim().localeCompare((b.name || "").trim(), undefined, { sensitivity: "base" })
    );
    return [...named, ...blank];
}

function sortDeviceComponents(components) {
    if (!components?.length) return components || [];
    return [...components].sort((a, b) =>
        (a.componentName || "").localeCompare(b.componentName || "", undefined, { sensitivity: "base" })
    );
}

function ManufacturingLabelContent({ device }) {
    if (!device) return null;
    return (
        <div
            style={{
                color: "#111",
                background: "#fff",
                fontFamily: "Arial, Helvetica, sans-serif",
                WebkitPrintColorAdjust: "exact",
                printColorAdjust: "exact",
            }}
        >
            <div
                className="manufacturing-label-cutout"
                style={{
                    border: "2px dashed #333",
                    padding: "10px 8px",
                    width: "2.75in",
                    boxSizing: "border-box",
                    marginBottom: "12px",
                    textAlign: "center",
                    background: "#fff",
                }}
            >
                <div
                    style={{
                        fontWeight: 700,
                        fontSize: "15px",
                        lineHeight: 1.25,
                        marginBottom: "10px",
                        wordBreak: "break-word",
                    }}
                >
                    {device.name}
                </div>
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <QRCode
                        value={deviceLabelUrl(device.manufacturedDeviceGuid)}
                        size={120}
                        level="M"
                        bgColor="#ffffff"
                        fgColor="#000000"
                    />
                </div>
            </div>

            <div
                className="manufacturing-label-reference"
                style={{ fontSize: "11px", maxWidth: "3.5in", lineHeight: 1.35 }}
            >
                {device.description ? (
                    <div style={{ marginBottom: "6px" }}>{device.description}</div>
                ) : null}
                <div>Manufactured: {formatDateDisplay(device.manufactureDate)}</div>
                <div>Built by: {device.builtBy}</div>
                {device.components?.length > 0 ? (
                    <div style={{ marginTop: "6px" }}>
                        Parts:{" "}
                        {sortDeviceComponents(device.components)
                            .map((c) => `${c.componentName} ×${c.quantity}`)
                            .join(", ")}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

const FORM_GRID_SX = {
    display: "grid",
    width: "100%",
    gap: 1.5,
    alignItems: "start",
    gridTemplateColumns: {
        xs: "repeat(2, minmax(80px, 1fr))",
        sm: "repeat(4, minmax(80px, 1fr))",
        md: "repeat(6, minmax(80px, 1fr))",
        lg: "repeat(8, minmax(80px, 1fr))",
    },
};

function ComponentNameField({ value, onChange, catalog }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const isLight = global.gw.theme === "light";
    const textPrimary = isLight ? "#333333" : "#f4cc8e";

    const sortedCatalog = useMemo(
        () =>
            [...catalog].sort((a, b) =>
                (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
            ),
        [catalog]
    );

    const filteredCatalog = useMemo(() => {
        const q = (value || "").trim().toLowerCase();
        if (!q) return sortedCatalog;
        return sortedCatalog.filter((c) => (c.name || "").toLowerCase().includes(q));
    }, [sortedCatalog, value]);

    const openPicker = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const closePicker = () => setAnchorEl(null);

    const pickComponent = (name) => {
        onChange(name);
        closePicker();
    };

    return (
        <>
            <MuiMaterials.TextField
                label="Component name"
                size="small"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                sx={{ minWidth: 80, width: "100%", ...comparisonFieldSx() }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <MuiMaterials.IconButton
                                onClick={openPicker}
                                edge="end"
                                size="small"
                                aria-label="Pick component from catalog"
                            >
                                <FormatListBulletedIcon fontSize="small" />
                            </MuiMaterials.IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={closePicker}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                    className: "hide-scrollbar",
                    sx: {
                        bgcolor: isLight ? "#ffffff" : "#2d2d2d",
                        color: textPrimary,
                        maxHeight: 360,
                        width: 380,
                        overflow: "hidden !important",
                        overflowY: "hidden !important",
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        "&::-webkit-scrollbar": {
                            display: "none !important",
                            width: 0,
                            height: 0,
                        },
                    },
                }}
            >
                <ScrollHintBody maxHeight={360} themeMode={isLight ? "light" : "dark"}>
                    <List
                        dense
                        disablePadding
                        className="hide-scrollbar"
                        sx={{
                            py: 0.5,
                            overflow: "visible",
                            maxHeight: "none",
                        }}
                    >
                        {filteredCatalog.length === 0 ? (
                            <ListItemText
                                sx={{ px: 2, py: 1, color: textPrimary }}
                                primary="No matching components"
                            />
                        ) : (
                            filteredCatalog.map((c) => (
                                <ListItemButton
                                    key={c.manufacturingComponentId ?? c.name}
                                    onClick={() => pickComponent(c.name)}
                                    sx={{ color: textPrimary }}
                                >
                                    <ListItemText
                                        primary={c.name}
                                        secondary={c.partNumber || null}
                                        primaryTypographyProps={{ sx: { color: textPrimary } }}
                                        secondaryTypographyProps={{
                                            sx: { color: isLight ? "#666666" : "#c4a574" },
                                        }}
                                    />
                                </ListItemButton>
                            ))
                        )}
                    </List>
                </ScrollHintBody>
            </Popover>
        </>
    );
}

const ManufacturingWindow = () => {
    const navigate = useNavigate();
    const [, setForceUpdate] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [devices, setDevices] = useState([]);
    const [productName, setProductName] = useState("");
    const [productDescription, setProductDescription] = useState("");
    const [builtBy, setBuiltBy] = useState(global.gw.email || "");
    const [manufactureDate, setManufactureDate] = useState(new Date());
    const [selectedLines, setSelectedLines] = useState([{ name: "", quantity: 1, notes: "" }]);
    const [labelDevice, setLabelDevice] = useState(null);
    const [catalogComponents, setCatalogComponents] = useState([]);
    const [editingDeviceGuid, setEditingDeviceGuid] = useState(null);
    const [deviceSearch, setDeviceSearch] = useState("");
    const [partsSearch, setPartsSearch] = useState("");

    const isAdmin = global.isAdminUser(global.gw.email);

    useEffect(() => {
        const handleThemeChange = () => setForceUpdate((p) => p + 1);
        window.addEventListener("themeChanged", handleThemeChange);
        return () => window.removeEventListener("themeChanged", handleThemeChange);
    }, []);

    const loadData = useCallback(async () => {
        if (!global.gw.token || !global.gw.email || !isAdmin) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError("");
        try {
            const compRes = await createApiEndpoint().fetchManufacturingComponents("direct");
            setCatalogComponents(compRes?.data?.fetchManufacturingComponents ?? []);

            const devRes = await createApiEndpoint().fetchManufacturedDevices("direct");
            setDevices(devRes?.data?.fetchManufacturedDevices ?? []);
        } catch (e) {
            console.warn("ManufacturingWindow: load failed", e);
            setError("Failed to load manufacturing data.");
        } finally {
            setLoading(false);
        }
    }, [isAdmin]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const filteredDevices = useMemo(() => {
        const q = deviceSearch.trim().toLowerCase();
        if (!q) return devices;
        return devices.filter((d) => {
            const haystack = [
                d.name,
                d.description,
                d.builtBy,
                formatDateDisplay(d.manufactureDate),
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return haystack.includes(q);
        });
    }, [devices, deviceSearch]);

    const filteredCatalogForParts = useMemo(() => {
        const q = partsSearch.trim().toLowerCase();
        if (!q) return catalogComponents;
        return catalogComponents.filter((c) => {
            const haystack = [c.name, c.partNumber, c.description]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return haystack.includes(q);
        });
    }, [catalogComponents, partsSearch]);

    const visiblePartLines = useMemo(() => {
        const q = partsSearch.trim().toLowerCase();
        return selectedLines
            .map((line, index) => ({ line, index }))
            .filter(({ line }) => {
                if (!q) return true;
                const haystack = [line.name, line.quantity, line.notes]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return haystack.includes(q);
            });
    }, [selectedLines, partsSearch]);

    const partsListedCount = useMemo(
        () => selectedLines.filter((line) => (line.name || "").trim()).length,
        [selectedLines]
    );

    const resetForm = () => {
        setEditingDeviceGuid(null);
        setProductName("");
        setProductDescription("");
        setSelectedLines([{ name: "", quantity: 1, notes: "" }]);
        setBuiltBy(global.gw.email || "");
        setManufactureDate(new Date());
    };

    const buildPayload = () => {
        const trimmedName = (productName || "").trim();
        const trimmedDescription = (productDescription || "").trim();
        const trimmedBuiltBy = (builtBy || "").trim();
        const componentLines = selectedLines
            .map((line) => ({
                name: (line.name || "").trim(),
                quantity: Number(line.quantity) || 1,
                notes: (line.notes || "").trim() || null,
            }))
            .filter((line) => line.name.length > 0);

        return {
            trimmedName,
            trimmedDescription,
            trimmedBuiltBy,
            componentLines,
            payload: {
                name: trimmedName,
                description: trimmedDescription || null,
                manufactureDate: manufactureDate.toISOString(),
                builtBy: trimmedBuiltBy,
                components: componentLines,
            },
        };
    };

    const addLineAfter = (index) => {
        setSelectedLines((prev) => {
            const next = [...prev];
            next.splice(index + 1, 0, { name: "", quantity: 1, notes: "" });
            return next;
        });
    };

    const updateLine = (index, field, value) => {
        setSelectedLines((prev) =>
            prev.map((line, i) => (i === index ? { ...line, [field]: value } : line))
        );
    };

    const removeLine = (index) => {
        setSelectedLines((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        if (!isAdmin) return;
        const { trimmedName, trimmedBuiltBy, payload } = buildPayload();
        if (!trimmedName) {
            setError("Name is required — what are you manufacturing?");
            return;
        }
        if (!trimmedBuiltBy) {
            setError("Built By is required.");
            return;
        }

        setSaving(true);
        setError("");
        try {
            if (editingDeviceGuid) {
                const savedGuid = editingDeviceGuid;
                const ok = await createApiEndpoint().updateManufacturedDevice("direct", {
                    ...payload,
                    manufacturedDeviceGuid: savedGuid,
                });
                if (!ok) {
                    setError("Could not update device record.");
                    return;
                }
                resetForm();
                await loadData();
                const detailRes = await createApiEndpoint().fetchManufacturedDevice("direct", savedGuid);
                const detail = detailRes?.data?.fetchManufacturedDevice;
                if (detail) setLabelDevice(detail);
            } else {
                const guid = await createApiEndpoint().addManufacturedDevice("direct", payload);
                if (!guid) {
                    setError("Could not save device record. Check fields and try again.");
                    return;
                }
                resetForm();
                await loadData();
                const detailRes = await createApiEndpoint().fetchManufacturedDevice("direct", guid);
                const detail = detailRes?.data?.fetchManufacturedDevice;
                if (detail) setLabelDevice(detail);
            }
        } catch (e) {
            console.warn("ManufacturingWindow: save failed", e);
            setError(editingDeviceGuid ? "Update failed." : "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const loadDeviceIntoForm = (detail, { forEdit }) => {
        if (forEdit) {
            setEditingDeviceGuid(detail.manufacturedDeviceGuid);
            const mDate = new Date(detail.manufactureDate);
            setManufactureDate(Number.isNaN(mDate.getTime()) ? new Date() : mDate);
            setBuiltBy(detail.builtBy || "");
        } else {
            setEditingDeviceGuid(null);
            setManufactureDate(new Date());
            setBuiltBy(global.gw.email || "");
        }
        setProductName(detail.name || "");
        setProductDescription(detail.description || "");
        const lines = detail.components?.length
            ? detail.components.map((c) => ({
                name: c.componentName || "",
                quantity: c.quantity || 1,
                notes: c.notes || "",
            }))
            : [{ name: "", quantity: 1, notes: "" }];
        setSelectedLines(forEdit ? sortPartLines(lines) : lines);
        const scrollEl = document.querySelector(".manufacturingWindow");
        if (scrollEl) {
            scrollEl.scrollTo({ top: 0, behavior: "smooth" });
        } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const startEditDevice = async (summary) => {
        setError("");
        try {
            const res = await createApiEndpoint().fetchManufacturedDevice(
                "direct",
                summary.manufacturedDeviceGuid
            );
            const detail = res?.data?.fetchManufacturedDevice;
            if (!detail) {
                setError("Could not load device for editing.");
                return;
            }
            loadDeviceIntoForm(detail, { forEdit: true });
        } catch (e) {
            console.warn("ManufacturingWindow: edit load failed", e);
            setError("Could not load device for editing.");
        }
    };

    const cloneDevice = async (summary) => {
        setError("");
        try {
            const res = await createApiEndpoint().fetchManufacturedDevice(
                "direct",
                summary.manufacturedDeviceGuid
            );
            const detail = res?.data?.fetchManufacturedDevice;
            if (!detail) {
                setError("Could not load device to clone.");
                return;
            }
            loadDeviceIntoForm(detail, { forEdit: false });
        } catch (e) {
            console.warn("ManufacturingWindow: clone load failed", e);
            setError("Could not load device to clone.");
        }
    };

    const openLabel = async (summary) => {
        try {
            const res = await createApiEndpoint().fetchManufacturedDevice(
                "direct",
                summary.manufacturedDeviceGuid
            );
            const detail = res?.data?.fetchManufacturedDevice;
            if (detail) setLabelDevice(detail);
        } catch (e) {
            console.warn("ManufacturingWindow: label load failed", e);
        }
    };

    const handlePrintLabel = () => {
        requestAnimationFrame(() => {
            window.print();
        });
    };

    if (!isAdmin) {
        return (
            <WindowChrome title="Manufacturing" onClose={() => navigate("/sites")}>
                <ModalBody>
                    <ModalContentTile>
                        <MuiMaterials.Typography>
                            Manufacturing is available to administrators only.
                        </MuiMaterials.Typography>
                    </ModalContentTile>
                </ModalBody>
            </WindowChrome>
        );
    }

    const tileSx = { flex: "none", overflow: "visible" };
    const fieldSx = comparisonFieldSx();
    const gridFieldSx = (span) => ({
        ...fieldSx,
        gridColumn: span,
        minWidth: 80,
        width: "100%",
    });

    return (
        <>
            <style>
                {`
                #manufacturing-label-print-portal {
                    position: fixed;
                    left: -10000px;
                    top: 0;
                    width: 3.5in;
                    padding: 0.25in;
                    background: #fff;
                    z-index: -1;
                }
                .manufacturing-label-cutout {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
                @media print {
                    @page { margin: 0.25in; }
                    body * { visibility: hidden !important; }
                    #manufacturing-label-print-portal,
                    #manufacturing-label-print-portal * {
                        visibility: visible !important;
                    }
                    #manufacturing-label-print-portal {
                        position: fixed !important;
                        left: 0 !important;
                        top: 0 !important;
                        width: 3.5in !important;
                        padding: 0.25in !important;
                        background: #fff !important;
                        z-index: 99999 !important;
                    }
                    .manufacturing-label-cutout {
                        border: 2px dashed #000 !important;
                        background: #fff !important;
                    }
                    .manufacturing-screen-only { display: none !important; }
                }
                `}
            </style>

            {labelDevice ? (
                <div id="manufacturing-label-print-portal" aria-hidden="true">
                    <ManufacturingLabelContent device={labelDevice} />
                </div>
            ) : null}

            <WindowChrome
                title="Manufacturing"
                className="manufacturing-screen-only"
                onClose={() => navigate("/sites")}
                loadingOverlay={loading ? <CenteredLoadingOverlay /> : null}
                statusBanner={
                    error ? (
                        <WindowChromeStatusBanner status={error} onDismiss={() => setError("")} />
                    ) : null
                }
                actions={
                    <>
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            onClick={handleSave}
                            disabled={saving}
                            sx={outlinedActionButtonSx()}
                        >
                            {saving
                                ? "Saving…"
                                : editingDeviceGuid
                                    ? "Save changes"
                                    : "Save and Create Label"}
                        </MuiMaterials.Button>
                        {editingDeviceGuid ? (
                            <MuiMaterials.Button
                                size="small"
                                variant="outlined"
                                onClick={resetForm}
                                disabled={saving}
                                sx={outlinedActionButtonSx()}
                            >
                                Cancel edit
                            </MuiMaterials.Button>
                        ) : null}
                    </>
                }
            >
                <ModalBody
                    className="manufacturingWindow hide-scrollbar"
                    sx={{ overflowY: "auto", overflowX: "hidden" }}
                >
                    {loading ? null : (
                        <>
                            <ModalContentTile sx={tileSx}>
                            <MuiMaterials.Box sx={FORM_GRID_SX}>
                                <MuiMaterials.Typography sx={{ fontWeight: 600, gridColumn: "1 / -1" }}>
                                    {editingDeviceGuid ? "Edit device record" : "New device record"}
                                </MuiMaterials.Typography>
                                <MuiMaterials.TextField
                                    label="Name"
                                    placeholder="What are you manufacturing?"
                                    size="small"
                                    required
                                    value={productName}
                                    onChange={(e) => setProductName(e.target.value)}
                                    sx={gridFieldSx({ xs: "1 / -1", sm: "span 2", md: "span 2", lg: "span 4" })}
                                />
                                <MuiMaterials.Box sx={{ gridColumn: { xs: "span 1", sm: "span 1", md: "span 2" }, minWidth: 80, width: "100%" }}>
                                    <MuiMaterials.LocalizationProvider dateAdapter={MuiMaterials.AdapterDateFns}>
                                        <MuiMaterials.DatePicker
                                            label="Manufacture date"
                                            value={manufactureDate}
                                            onChange={(v) => v && setManufactureDate(v)}
                                            slotProps={{ textField: { size: "small", fullWidth: true, sx: fieldSx } }}
                                        />
                                    </MuiMaterials.LocalizationProvider>
                                </MuiMaterials.Box>
                                <MuiMaterials.TextField
                                    label="Built by"
                                    size="small"
                                    value={builtBy}
                                    onChange={(e) => setBuiltBy(e.target.value)}
                                    sx={gridFieldSx({ xs: "span 1", sm: "span 1", md: "span 2" })}
                                />
                                <MuiMaterials.TextField
                                    label="Description"
                                    placeholder="Brief description of this build"
                                    size="small"
                                    multiline
                                    minRows={2}
                                    value={productDescription}
                                    onChange={(e) => setProductDescription(e.target.value)}
                                    sx={gridFieldSx("1 / -1")}
                                />

                                <MuiMaterials.Box
                                    sx={{
                                        gridColumn: "1 / -1",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 2,
                                    }}
                                >
                                    <MuiMaterials.Typography variant="subtitle2">
                                        Parts used (optional)
                                    </MuiMaterials.Typography>
                                    <MuiMaterials.Typography
                                        sx={{
                                            fontSize: "2rem",
                                            fontWeight: 700,
                                            lineHeight: 1,
                                            minWidth: "2.5rem",
                                            textAlign: "right",
                                        }}
                                    >
                                        {partsListedCount}
                                    </MuiMaterials.Typography>
                                </MuiMaterials.Box>
                                <MuiMaterials.TextField
                                    placeholder="Search parts or catalog…"
                                    size="small"
                                    value={partsSearch}
                                    onChange={(e) => setPartsSearch(e.target.value)}
                                    sx={gridFieldSx("1 / -1")}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {partsSearch.trim() && visiblePartLines.length === 0 ? (
                                    <MuiMaterials.Typography variant="body2" sx={{ gridColumn: "1 / -1", color: "text.secondary" }}>
                                        No parts on this build match your search.
                                    </MuiMaterials.Typography>
                                ) : null}
                                {visiblePartLines.map(({ line, index }) => (
                                    <MuiMaterials.Box
                                        key={`part-line-${index}`}
                                        sx={{
                                            ...FORM_GRID_SX,
                                            gridColumn: "1 / -1",
                                            alignItems: "center",
                                        }}
                                    >
                                        <MuiMaterials.Box sx={{ gridColumn: { xs: "1 / -1", md: "span 3" }, minWidth: 80, width: "100%" }}>
                                            <ComponentNameField
                                                value={line.name}
                                                onChange={(name) => updateLine(index, "name", name)}
                                                catalog={filteredCatalogForParts}
                                            />
                                        </MuiMaterials.Box>
                                        <MuiMaterials.TextField
                                            label="Qty"
                                            type="number"
                                            size="small"
                                            inputProps={{ min: 1 }}
                                            value={line.quantity}
                                            onChange={(e) => updateLine(index, "quantity", e.target.value)}
                                            sx={gridFieldSx("span 1")}
                                        />
                                        <MuiMaterials.TextField
                                            label="Notes"
                                            size="small"
                                            value={line.notes}
                                            onChange={(e) => updateLine(index, "notes", e.target.value)}
                                            sx={gridFieldSx({ xs: "1 / -1", sm: "span 2" })}
                                        />
                                        <MuiMaterials.Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => addLineAfter(index)}
                                            sx={{ ...outlinedActionButtonSx(), gridColumn: "span 1", minWidth: 80 }}
                                        >
                                            Add
                                        </MuiMaterials.Button>
                                        <MuiMaterials.Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => removeLine(index)}
                                            disabled={selectedLines.length === 1}
                                            sx={{ ...outlinedActionButtonSx(), gridColumn: "span 1", minWidth: 80 }}
                                        >
                                            Remove
                                        </MuiMaterials.Button>
                                    </MuiMaterials.Box>
                                ))}
                            </MuiMaterials.Box>
                            </ModalContentTile>

                            <ModalContentTile sx={tileSx}>
                            <MuiMaterials.Box sx={FORM_GRID_SX}>
                            <MuiMaterials.Typography sx={{ fontWeight: 600, gridColumn: "1 / -1" }}>Recent devices</MuiMaterials.Typography>
                            <MuiMaterials.TextField
                                placeholder="Search name, description, built by, date…"
                                size="small"
                                value={deviceSearch}
                                onChange={(e) => setDeviceSearch(e.target.value)}
                                sx={gridFieldSx("1 / -1")}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <MuiMaterials.Box sx={{ gridColumn: "1 / -1", minWidth: 0, width: "100%", overflowX: "auto" }}>
                            {devices.length === 0 ? (
                                <MuiMaterials.Typography variant="body2">No devices recorded yet.</MuiMaterials.Typography>
                            ) : filteredDevices.length === 0 ? (
                                <MuiMaterials.Typography variant="body2">No devices match your search.</MuiMaterials.Typography>
                            ) : (
                                <MuiMaterials.Table size="small">
                                    <MuiMaterials.TableHead>
                                        <MuiMaterials.TableRow>
                                            <MuiMaterials.TableCell>Name</MuiMaterials.TableCell>
                                            <MuiMaterials.TableCell>Date</MuiMaterials.TableCell>
                                            <MuiMaterials.TableCell>Built by</MuiMaterials.TableCell>
                                            <MuiMaterials.TableCell align="right">Actions</MuiMaterials.TableCell>
                                        </MuiMaterials.TableRow>
                                    </MuiMaterials.TableHead>
                                    <MuiMaterials.TableBody>
                                        {filteredDevices.map((d) => (
                                            <MuiMaterials.TableRow key={d.manufacturedDeviceGuid}>
                                                <MuiMaterials.TableCell>{d.name || "—"}</MuiMaterials.TableCell>
                                                <MuiMaterials.TableCell>{formatDateDisplay(d.manufactureDate)}</MuiMaterials.TableCell>
                                                <MuiMaterials.TableCell>{d.builtBy}</MuiMaterials.TableCell>
                                                <MuiMaterials.TableCell align="right">
                                                    <MuiMaterials.Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                                                        <MuiMaterials.Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<ContentCopyIcon />}
                                                            onClick={() => cloneDevice(d)}
                                                            sx={outlinedActionButtonSx()}
                                                        >
                                                            Clone
                                                        </MuiMaterials.Button>
                                                        <MuiMaterials.Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<EditIcon />}
                                                            onClick={() => startEditDevice(d)}
                                                            sx={outlinedActionButtonSx()}
                                                        >
                                                            Edit
                                                        </MuiMaterials.Button>
                                                        <MuiMaterials.Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => openLabel(d)}
                                                            sx={outlinedActionButtonSx()}
                                                        >
                                                            Print label
                                                        </MuiMaterials.Button>
                                                    </MuiMaterials.Stack>
                                                </MuiMaterials.TableCell>
                                            </MuiMaterials.TableRow>
                                        ))}
                                    </MuiMaterials.TableBody>
                                </MuiMaterials.Table>
                            )}
                            </MuiMaterials.Box>
                            </MuiMaterials.Box>
                            </ModalContentTile>
                        </>
                    )}
                </ModalBody>
            </WindowChrome>

            {labelDevice ? (
                <MuiMaterials.Modal
                    className="manufacturing-screen-only"
                    open={Boolean(labelDevice)}
                    onClose={() => setLabelDevice(null)}
                >
                    <MuiMaterials.Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            bgcolor: "#fff",
                            color: "#111",
                            p: 3,
                            borderRadius: 1,
                            maxWidth: 420,
                        }}
                    >
                        <ManufacturingLabelContent device={labelDevice} />
                        <MuiMaterials.Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                            <MuiMaterials.Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={handlePrintLabel}>
                                Print
                            </MuiMaterials.Button>
                            <MuiMaterials.Button size="small" variant="outlined" onClick={() => setLabelDevice(null)}>Close</MuiMaterials.Button>
                        </MuiMaterials.Stack>
                    </MuiMaterials.Box>
                </MuiMaterials.Modal>
            ) : null}
        </>
    );
};

export default ManufacturingWindow;
